// src/components/Sidebar.js
import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

// `perm` = the permission required to see the item. `anyOf` = show if the user
// holds any listed permission (Settings surfaces several admin areas).
const NAV = [
  { to: '/', icon: 'ti-layout-dashboard', label: 'Dashboard', perm: 'dashboard.view' },
  { to: '/leads', icon: 'ti-users', label: 'All Leads', perm: 'leads.view' },
  { to: '/followups', icon: 'ti-calendar-event', label: 'Follow-ups', perm: 'leads.view' },
  { to: '/forms', icon: 'ti-forms', label: 'Forms & Webhooks', perm: 'forms.view' },
  { to: '/autoresponse', icon: 'ti-message-bolt', label: 'Auto-Response', perm: 'autoresponse.view' },
  { to: '/import', icon: 'ti-file-import', label: 'Import Leads', perm: 'leads.import' },
  { to: '/settings', icon: 'ti-settings', label: 'Settings', anyOf: ['settings.manage', 'users.view', 'users.manage', 'roles.manage'] },
];

export default function Sidebar({ pendingFU = 0, totalLeads = 0 }) {
  const { user, signOut, can, canAny } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const badges = { '/leads': totalLeads, '/followups': pendingFU };
  const navItems = NAV.filter(n => (n.anyOf ? canAny(n.anyOf) : can(n.perm)));
  const canOpenSettings = canAny('settings.manage', 'users.view', 'users.manage', 'roles.manage');

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div className="sidebar">
      <div className="sidebar-logo">
        <div className="brand">
          {branding.logo_url ? <img className="logo-img" src={branding.logo_url} alt={branding.brand_name} /> : <span className="logo-name">{branding.brand_name}</span>}
        </div>
        <div className="sub">EMS v1.0</div>
      </div>

      <nav className="sidebar-nav">
        {navItems.map(({ to, icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
          >
            <i className={`ti ${icon}`} />
            <span className="nav-label">{label}</span>
            {badges[to] > 0 && (
              <span className={`nav-badge${to === '/followups' ? ' amber' : ''}`}>{badges[to]}</span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer" style={{ position: 'relative' }} ref={menuRef}>
        {menuOpen && (
          <div style={{
            position: 'absolute', bottom: '100%', left: 8, right: 8, background: 'var(--white)',
            border: '0.5px solid var(--gray-200)', borderRadius: 10, padding: 6, marginBottom: 4,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 100,
          }}>
            <button
              onClick={() => { setMenuOpen(false); navigate('/settings'); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'none', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, color: 'var(--gray-600)', fontFamily: 'var(--font)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <i className="ti ti-key" style={{ fontSize: 15 }} /> Change Password
            </button>
            {canOpenSettings && (
              <button
                onClick={() => { setMenuOpen(false); navigate('/settings'); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'none', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, color: 'var(--gray-600)', fontFamily: 'var(--font)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <i className="ti ti-settings" style={{ fontSize: 15 }} /> Settings
              </button>
            )}
            <div style={{ height: '0.5px', background: 'var(--gray-100)', margin: '4px 0' }} />
            <button
              onClick={() => { setMenuOpen(false); signOut(); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'none', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, color: 'var(--red)', fontFamily: 'var(--font)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--red-light)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <i className="ti ti-logout" style={{ fontSize: 15 }} /> Sign Out
            </button>
          </div>
        )}
        <div className="user-pill" onClick={() => setMenuOpen(o => !o)} title="Account menu">
          <div className="avatar">{user?.avatar_initials || user?.name?.slice(0, 2).toUpperCase() || 'U'}</div>
          <div className="user-info">
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--black)' }}>{user?.name}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{user?.role_name || user?.role}</div>
          </div>
          <i className="ti ti-dots-vertical" style={{ marginLeft: 'auto', fontSize: 15, color: 'var(--gray-400)' }} />
        </div>
      </div>
    </div>
  );
}
